import React from 'react';
import PropTypes from 'prop-types';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import Avatar from '@mui/material/Avatar';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PhoneIcon from '@mui/icons-material/Phone';
import HomeIcon from '@mui/icons-material/Home';
import SchoolIcon from '@mui/icons-material/School';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

const ResultCard = ({ data, type }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const isOrganization = type === 'organization';
  const Icon = isOrganization ? SchoolIcon : DirectionsCarIcon;
  const chipColor = isOrganization ? 'primary' : 'secondary';
  const chipLabel = isOrganization ? 'Driving School' : 'Car Dealer';

  // Check for image in common field names
  const imageUrl = data.logo;
  return (
    <Card
      sx={{
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        borderRadius: 2,
        border: `1px solid ${theme.palette.divider}`,
        boxShadow: theme.shadows[6],
        transition: 'all 0.3s ease-in-out',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: theme.shadows[8],
        },
      }}
    >
      {/* Left Section - Image or Avatar */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: imageUrl ? 'grey.100' : (isOrganization ? theme.palette.primary.light : theme.palette.secondary.light),
          width: isMobile ? '100%' : 160,
          minWidth: isMobile ? 'auto' : 160,
          height: isMobile ? 140 : 'auto',
          minHeight: isMobile ? 140 : 160,
          overflow: 'hidden',
          flexShrink: 0,
          alignSelf: 'stretch',
        }}
      >
        {imageUrl ? (
          <Box
            component="img"
            src={imageUrl}
            alt={data.name}
            onError={(e) => {
              e.target.style.display = 'none';
              e.target.nextSibling.style.display = 'flex';
            }}
            sx={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
            }}
          />
        ) : null}
        <Avatar
          sx={{
            bgcolor: isOrganization ? theme.palette.primary.main : theme.palette.secondary.main,
            width: isMobile ? 48 : 64,
            height: isMobile ? 48 : 64,
            display: imageUrl ? 'none' : 'flex',
          }}
        >
          <Icon sx={{ fontSize: isMobile ? 28 : 36 }} />
        </Avatar>
      </Box>

      {/* Main Content */}
      <CardContent
        sx={{
          flexGrow: 1,
          p: { xs: 2, sm: 3 },
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Header Row - Name and Badge */}
        <Box
          display="flex"
          flexDirection={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          gap={1}
          mb={1.5}
        >
          <Typography
            variant="h6"
            component="h3"
            sx={{
              fontWeight: 700,
              fontSize: { xs: '1rem', sm: '1.25rem' },
            }}
          >
            {data.name}
          </Typography>
          <Chip
            label={chipLabel}
            color={chipColor}
            size="small"
            sx={{ fontWeight: 600, flexShrink: 0 }}
          />
        </Box>

        {/* Address */}
        <Box display="flex" alignItems="center" mb={1}>
          <HomeIcon sx={{ fontSize: 18, color: 'text.secondary', mr: 0.5, flexShrink: 0 }} />
          <Typography
            variant="body2"
            color="text.secondary"
            noWrap
            sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}
          >
            {data.address}{data.pincode ? `, ${data.pincode}` : ''}
          </Typography>
        </Box>

        {/* Location */}
        <Box display="flex" alignItems="center" mb={1}>
          <LocationOnIcon sx={{ fontSize: 18, color: 'text.secondary', mr: 0.5, flexShrink: 0 }} />
          <Typography variant="body2" color="text.secondary" noWrap>
            {data.city}, {data.state}
          </Typography>
        </Box>

        {/* Phone */}
        <Box display="flex" alignItems="center" mb={1}>
          <PhoneIcon sx={{ fontSize: 18, color: 'text.secondary', mr: 0.5, flexShrink: 0 }} />
          <Typography
            variant="body2"
            color="primary"
            component="a"
            href={`tel:${data.phoneNumber}`}
            sx={{ textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
            noWrap
          >
            +91 {data.phoneNumber}
          </Typography>
        </Box>

        {/* Google Review Link */}
        {data.googleReviewLink && (
          <Box display="flex" alignItems="center">
            <Typography
              variant="body2"
              color="primary"
              component="a"
              href={data.googleReviewLink}
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                textDecoration: 'none',
                display: 'flex',
                alignItems: 'center',
                fontWeight: 600,
                '&:hover': { textDecoration: 'underline' },
              }}
            >
              View on Google
              <OpenInNewIcon sx={{ fontSize: 16, ml: 0.5 }} />
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

ResultCard.propTypes = {
  data: PropTypes.shape({
    _id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    phoneNumber: PropTypes.string.isRequired,
    address: PropTypes.string,
    city: PropTypes.string.isRequired,
    state: PropTypes.string.isRequired,
    pincode: PropTypes.string,
    license: PropTypes.string,
    label: PropTypes.string,
    logo: PropTypes.string,
    image: PropTypes.string,
    profileImage: PropTypes.string,
    googleReviewLink: PropTypes.string,
  }).isRequired,
  type: PropTypes.oneOf(['organization', 'dealer']).isRequired,
};

export default ResultCard;
